import React, { useState, useEffect } from 'react';
import styled, { ThemeProvider } from 'styled-components';
import PropTypes from 'prop-types';
import { Route, Redirect } from 'react-router-dom';
import { Flex } from 'rebass/styled-components';
import { Nav, AccountInfo } from '../sections';
import {
  Breadcrumbs, ConfirmModal, FeatureGate, Loading,
} from '../components';
import PageNotEnabled from './PageNotEnabled';
import theme from '../theme';
import AnnouncementButton from '../components/announcement-button';
import { useAppState } from '../hooks';
import { CcsAccountType } from '../enums';

const ContentWrapper = styled(Flex)`
  overflow: auto;
`;

function AuthenticatedRoute(props) {
  const {
    isAuthed,
    location,
    component,
    path,
    userId,
    feature,
  } = props;

  const {
    isLoading, accountId: brandId, themeMode, logout, ccsAccountType, refresh, features,
  } = useAppState();

  const [confirmModalProps, setConfirmModalProps] = useState({ show: false });

  const Component = component;

  useEffect(() => {
    if (!isLoading && isAuthed && (ccsAccountType === CcsAccountType.INVALID || !features)) {
      setConfirmModalProps({
        title: 'Unable to verify credentials',
        show: true,
        detail: 'Please click "Try again" to re-verify or "Logout" to restart authentication',
        onConfirm: refresh,
        onCancel: logout,
        confirmText: 'Try again',
        cancelText: 'Logout',
      });
    } else {
      setConfirmModalProps({ show: false });
    }
  }, [isLoading, ccsAccountType, refresh, logout, features, isAuthed]);

  if (!isAuthed && location !== path) {
    return (
      <Redirect
        data-testid="redirect-route"
        to={{
          pathname: '/login',
          state: { from: location },
        }}
      />
    );
  }

  const content = () => {
    if (isLoading) {
      return (
        <Flex
          sx={{ width: '100%', height: '100%' }}
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <Loading size={24} />
        </Flex>
      );
    }

    if (ccsAccountType !== CcsAccountType.INVALID) {
      return (
        <>
          <Flex justifyContent="space-between" flexShrink={0}>
            <Breadcrumbs />
            <AccountInfo
              userId={userId}
              brandId={brandId}
              themeMode={themeMode}
            />
            <AnnouncementButton />
          </Flex>
          <Route
            render={(routerProps) => (
              <FeatureGate
                feature={feature}
                fallback={<PageNotEnabled />}
              >
                <Component {...props} {...routerProps} />
              </FeatureGate>
            )}
          />
        </>
      );
    }

    return null;
  };

  return (
    <ThemeProvider theme={theme}>
      <>
        <Nav brandId={brandId} path={path} />
        <ContentWrapper
          pb={4}
          p={4}
          flex={1}
          data-testid="authenticated-route"
          flexDirection="column"
        >
          <>
            {content()}
            <ConfirmModal {...confirmModalProps} />
          </>
        </ContentWrapper>
      </>
    </ThemeProvider>
  );
}

AuthenticatedRoute.defaultProps = {
  location: undefined,
  component: undefined,
  path: undefined,
  userId: null,
  feature: undefined,
};

AuthenticatedRoute.propTypes = {
  userId: PropTypes.string,
  path: PropTypes.string,
  isAuthed: PropTypes.bool.isRequired,
  component: PropTypes.func,
  location: PropTypes.object,
  feature: PropTypes.string,
};

export default AuthenticatedRoute;
